import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { LibraryPage } from '@modules/report-generator/interfaces';

interface PageSection {
  slug: string;
  name: string;
}

@Component({
  selector: 'app-page-grid',
  templateUrl: './page-grid.component.html',
  styleUrls: ['./page-grid.component.scss'],
})
export class PageGridComponent implements OnInit {
  @Input() pages: LibraryPage[] = [];
  @Input() testType?: string;
  @Input() initialChip = 'all';
  @Output() pageSelected = new EventEmitter<LibraryPage>();
  @Output() chipSelected = new EventEmitter<string>();

  searchControl = new FormControl('');
  selectedChip = 'all';
  sections: PageSection[] = [];
  filteredPages: LibraryPage[] = [];

  ngOnInit(): void {
    this.selectedChip = this.initialChip;
    this.buildSections();
    this.applyFilters();

    this.searchControl.valueChanges.subscribe(() => {
      this.applyFilters();
    });
  }

  onChipSelect(sectionSlug: string): void {
    this.selectedChip = sectionSlug;
    this.chipSelected.emit(sectionSlug);
    this.applyFilters();
  }

  onPageClick(page: LibraryPage): void {
    this.pageSelected.emit(page);
  }

  getThumbnailAspectRatio(page: LibraryPage): string {
    return page.orientation === 'landscape' ? '842/595' : '595/842';
  }

  getThumbnailMaxWidth(page: LibraryPage): string {
    return page.orientation === 'landscape' ? '170px' : '120px';
  }

  private buildSections(): void {
    const sectionMap = new Map<string, string>();

    this.pages.forEach((page) => {
      if (!sectionMap.has(page.section_name)) {
        sectionMap.set(page.section_name, this.formatSectionName(page.section_name));
      }
    });

    this.sections = Array.from(sectionMap.entries()).map(([slug, name]) => ({
      slug,
      name,
    }));
  }

  private applyFilters(): void {
    let filtered = this.pages;

    // Apply test type filter
    if (this.testType) {
      const slugPrefix = `test_data_${this.testType}`;
      filtered = filtered.filter((page) => {
        if (page.section_name !== 'Test Data') {
          return page;
        }
        return page.slug.startsWith(slugPrefix);
      });
    }

    // Apply chip filter
    if (this.selectedChip !== 'all') {
      filtered = filtered.filter((page) => page.section_name === this.selectedChip);
    }

    // Apply search filter
    const searchTerm = this.searchControl.value?.toLowerCase() || '';
    if (searchTerm) {
      filtered = filtered.filter(
        (page) =>
          page.name.toLowerCase().includes(searchTerm) ||
          page.description?.toLowerCase().includes(searchTerm) ||
          page.section_name.toLowerCase().includes(searchTerm)
      );
    }

    this.filteredPages = filtered;
  }

  private formatSectionName(slug: string): string {
    return slug
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}
